<template>
  <VTooltip v-bind="tooltipOptions" ref="vTooltip" v-model:shown="tooltipShow" :triggers="[]" @apply-show="applyShow">
    <template #popper>{{ tooltipOptions?.content }}</template>
  </VTooltip>
</template>

<script setup lang="ts">
import { useElementHover } from '@vueuse/core';
import { ref, watch } from 'vue';
import { nextFrame } from '@/components/p-tooltip/p-tooltip.helpers';
import { pTooltipInstanceExists, usePTooltip } from '@/components/p-tooltip/usePTooltip';

defineOptions({
  name: 'PTooltip',
});

type VTooltip = {
  $refs: {
    popper: {
      $_referenceNode: HTMLElement;
      $_popperNode: HTMLElement;
      $_detachPopperNode: () => void;
      isShown: boolean;
      $emit: (event: string, ...args: unknown[]) => void;
    };
  };
};

const vTooltip = ref<VTooltip | null>(null);
const tooltipShow = ref(false);
const { tooltipOptions, shouldTooltipShow, tooltipTrigger, setIsTooltipHovered, hideTooltip } = usePTooltip();
const popperNode = ref<HTMLElement | null>(null);
const isPopperNodeHovered = useElementHover(popperNode);

const applyShow = () => {
  popperNode.value = vTooltip.value?.$refs?.popper?.$_popperNode as HTMLElement;
};

watch(isPopperNodeHovered, (nV) => {
  if (nV === true) {
    setIsTooltipHovered(true);
  } else {
    setIsTooltipHovered(false);
    hideTooltip();
  }
});

// Shows or hides the tooltip
const updateReference = async (newReference: HTMLElement | null) => {
  const popper = vTooltip.value?.$refs?.popper;
  const hasNewReference = newReference !== null;

  if (popper) {
    // Detach the popper node
    popper.$_detachPopperNode();

    // If the tooltip is shown, hide it
    if (hasNewReference && popper.isShown) {
      popper.$emit('update:shown', false);
    }

    // Wait for the popper to detach the popper node
    await nextFrame();

    popper.$_referenceNode = hasNewReference ? newReference : document.body;

    // Wait for the popper to update the reference
    await nextFrame();

    popper.$emit('update:shown', hasNewReference);
  }
};

// Watch for the tooltip's show state and show or hide the tooltip accordingly
watch(shouldTooltipShow, async (nV) => {
  if (nV && tooltipTrigger.value) {
    updateReference(tooltipTrigger.value);
  } else {
    updateReference(null);
  }
});

// Check if the PTooltip instance already exists
if (pTooltipInstanceExists.value) {
  throw new Error('PTooltip can only be used once per application. Remove duplicate instances.');
}

pTooltipInstanceExists.value = true;
</script>

<style>
.p-tooltip-stay-open-on-hover {
  & .v-popper__wrapper {
    border: 12px solid transparent;
  }

  &[data-popper-placement^='bottom'] {
    & .v-popper__arrow-container {
      top: 12px;
    }
  }

  &[data-popper-placement^='left'] {
    & .v-popper__arrow-container {
      right: 2px;
    }
  }
}
</style>
